// Client-side session flag for the admin SPA, kept in localStorage.
//
// This is a UX gate, NOT a security boundary:
//   - In dev (localhost) the API has DEV_ADMIN_BYPASS=1 and authorizes every
//     request. Without this flag the admin would auto-load on every fresh
//     visit (including incognito), which makes "log out" feel impossible to
//     test. Treating dev without an explicit `'in'` flag as signed-out gives
//     a believable login flow even though the API isn't actually authenticating.
//   - In prod (admin.fuelio.net) Cloudflare Access is the real gate. We only
//     act on the explicit `'out'` value here so a fresh visit (no flag) goes
//     straight through CF Access' real auth, no extra "click to sign in" step.
//
// Stored values:
//   '' / null  → no explicit state. Treated as signed-in in prod, signed-out in dev.
//   'in'        → user has explicitly signed in (last sign-in click)
//   'out'       → user has explicitly signed out
//
// Why we store 'in' explicitly rather than just clearing the key: in dev the
// gate's default is "out", so we need an affirmative signal to let the user in.

const KEY = 'fuelio.admin.session'

/** True when the SPA is running on a localhost dev server. */
export function isLocalDev(): boolean {
  if (typeof window === 'undefined') return false
  return /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
}

export function isSignedOut(): boolean {
  if (typeof window === 'undefined') return false
  const v = localStorage.getItem(KEY)
  if (v === 'out') return true
  // In dev, no explicit 'in' means we should send the user through the login
  // screen first — otherwise DEV_ADMIN_BYPASS makes "log out" untestable.
  if (isLocalDev() && v !== 'in') return true
  return false
}

// 登入 / 登出会切换身份 —— 必须把 stale-while-revalidate 缓存（whoami /
// menus 等）清掉，否则下一个用户首屏会看到上一个用户的邮箱 + 菜单 overlay。
function clearAdminSessionCache(): void {
  if (typeof sessionStorage === 'undefined') return
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i)
      if (k && k.startsWith('admin.')) sessionStorage.removeItem(k)
    }
  } catch { /* ignore */ }
}

export function markSignedOut(): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(KEY, 'out')
  clearAdminSessionCache()
}

export function markSignedIn(): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(KEY, 'in')
  clearAdminSessionCache()
}
